import React, { useState, useEffect } from 'react';
import { CheckCircle, ArrowSquareOut, Warning, CurrencyCircleDollar, Clock } from '@phosphor-icons/react/ssr';
import { api } from './services/apiClient';

const fmt = (n) => n > 0
  ? new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(n)
  : 'Gratis';

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })
  : null;

const BillingModule = ({ useAuth, useNotification, BaseButton, BaseCard }) => {
  const { user }           = useAuth();
  const { addNotification } = useNotification();

  // ===== TOGGLE: Cambiar a 'false' para mostrar el módulo real =====
  const SHOW_UNDER_CONSTRUCTION = true;
  // ================================================================

  const [status, setStatus]   = useState(null);
  const [plans, setPlans]     = useState([]);
  const [cycle, setCycle]     = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [paying, setPaying]   = useState(null); // nombre del plan en proceso

  useEffect(() => {
    Promise.all([
      api.get('/api/billing/status'),
      api.get('/api/billing/plans')
    ])
      .then(([s, p]) => { setStatus(s); setPlans(p.plans ?? []); })
      .catch(() => addNotification('Error al cargar información de facturación', 'error'))
      .finally(() => setLoading(false));
  }, []);

  const handleCheckout = async (planName) => {
    setPaying(planName);
    try {
      const { checkoutUrl } = await api.post('/api/billing/checkout', { planName, billingCycle: cycle });
      window.open(checkoutUrl, '_blank');
    } catch (err) {
      addNotification(err.message || 'No se pudo iniciar el proceso de pago', 'error');
    } finally {
      setPaying(null);
    }
  };

  // ===== VISTA: EN CONSTRUCCIÓN =====
  if (SHOW_UNDER_CONSTRUCTION) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header con icono animado */}
        <div className="text-center space-y-4 py-8">
          <div className="inline-flex p-4 rounded-full" style={{ background: 'var(--primary-light, #eff6ff)' }}>
            <Clock size={48} className="animate-spin-slow" style={{ color: 'var(--primary)' }} />
          </div>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
            Módulo en Construcción
          </h2>
          <p className="text-sm max-w-md mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Estamos trabajando en una experiencia de facturación mejorada para tu empresa.
          </p>
        </div>

        {/* Card principal */}
        <div className="rounded-xl p-6 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg" style={{ background: 'var(--primary-light, #eff6ff)' }}>
              <CurrencyCircleDollar size={24} style={{ color: 'var(--primary)' }} />
            </div>
            <div className="flex-1 space-y-3">
              <h3 className="font-semibold" style={{ color: 'var(--text)' }}>
                ¿Necesitas cambiar tu plan ahora?
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Mientras finalizamos este módulo, puedes contactar al administrador de{' '}
                <strong>GEOCheck 10S</strong> para gestionar tu plan actual.
              </p>
              <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
                <Warning size={16} />
                <span>El módulo de pagos estará disponible próximamente con nuevas funcionalidades.</span>
              </div>
            </div>
          </div>
        </div>

        {/* Plan actual (si existe) */}
        {status && (
          <div className="rounded-xl p-5 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-secondary)' }}>
              Plan actual
            </p>
            <div className="flex items-center gap-3">
              <span className="text-xl font-bold capitalize" style={{ color: 'var(--text)' }}>
                {{ trial: 'Prueba gratuita', basic: 'Básico', enterprise: 'Empresarial' }[status.plan] ?? status.plan}
              </span>
            </div>
            {status.planEndDate && (
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                Vigente hasta: {new Date(status.planEndDate).toLocaleDateString('es-CO')}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  // ===== VISTA: MÓDULO REAL (código original) =====
  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Cargando planes…</p>
    </div>
  );

  const paymentsEnabled = plans.length > 0 && status?.paymentsEnabled;

  return (
    <div className="max-w-3xl space-y-6">
      {/* Título */}
      <div>
        <h2 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>Facturación y planes</h2>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Administra el plan de tu empresa
        </p>
      </div>

      {/* Plan actual */}
      {status && (
        <div className="rounded-xl p-5 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-secondary)' }}>
            Plan actual
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xl font-bold capitalize" style={{ color: 'var(--text)' }}>
              {{ trial: 'Prueba gratuita', basic: 'Básico', enterprise: 'Empresarial' }[status.plan] ?? status.plan}
            </span>
            {status.isPlanExpired && (
              <span className="text-sm font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded border border-red-200">
                Vencido
              </span>
            )}
            {status.isPlanExpiringSoon && !status.isPlanExpired && (
              <span className="text-sm font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                Vence en {status.planDaysLeft} día{status.planDaysLeft !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {status.planEndDate && (
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              {status.isPlanExpired ? 'Venció el' : 'Vigente hasta:'} {fmtDate(status.planEndDate)}
            </p>
          )}
          {status.isPlanExpired && (
            <div className="mt-3 flex items-start gap-2 text-sm text-red-700 bg-red-50 rounded-lg px-4 py-3 border border-red-200">
              <Warning size={16} className="flex-shrink-0 mt-0.5" />
              Tu plan ha vencido. No puedes ejecutar validaciones hasta renovarlo.
            </div>
          )}
        </div>
      )}

      {/* Aviso si pagos no están habilitados */}
      {!paymentsEnabled && (
        <div className="flex items-start gap-3 rounded-lg px-4 py-3 text-sm"
          style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #fcd34d' }}>
          <Warning size={18} className="flex-shrink-0 mt-0.5" />
          <span>
            El módulo de pagos aún no está activado. Cuando esté disponible podrás mejorar tu plan desde aquí.
            Por ahora contacta al administrador de <strong>GEOCheck 10S</strong> para cambiar tu plan.
          </span>
        </div>
      )}

      {/* Selector de ciclo */}
      {paymentsEnabled && (
        <>
          <div className="flex items-center gap-1 rounded-lg overflow-hidden border self-start w-fit"
            style={{ borderColor: 'var(--border)' }}>
            {[
              { v: 'monthly', l: 'Mensual' },
              { v: 'annual',  l: 'Anual (con descuento)' }
            ].map(({ v, l }) => (
              <button key={v}
                onClick={() => setCycle(v)}
                className="px-4 py-2 text-sm font-medium transition-colors"
                style={{
                  background: cycle === v ? 'var(--primary)' : 'var(--surface)',
                  color:      cycle === v ? '#fff'           : 'var(--text-secondary)'
                }}>
                {l}
              </button>
            ))}
          </div>

          {/* Tarjetas de planes */}
          <div className="grid sm:grid-cols-3 gap-4">
            {plans.map(plan => {
              const price    = cycle === 'annual' ? plan.annualAmountCop : plan.monthlyAmountCop;
              const isCurrent = status?.plan === plan.name;

              return (
                <div key={plan.name}
                  className="rounded-xl p-5 flex flex-col gap-3 border transition-all"
                  style={{
                    background:   isCurrent ? 'var(--primary-light, #eff6ff)' : 'var(--surface)',
                    borderColor:  isCurrent ? 'var(--primary)' : 'var(--border)',
                    boxShadow:    isCurrent ? '0 0 0 2px var(--primary)' : 'none'
                  }}>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-1"
                      style={{ color: 'var(--text-secondary)' }}>{plan.displayName}</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
                      {fmt(price)}
                    </p>
                    {price > 0 && (
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        por empresa / {cycle === 'annual' ? 'año' : 'mes'}
                      </p>
                    )}
                  </div>

                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {plan.maxUsers === 0 ? 'Usuarios ilimitados' : `Hasta ${plan.maxUsers} usuarios`}
                  </p>

                  {isCurrent ? (
                    <div className="flex items-center gap-1.5 text-sm font-medium" style={{ color: 'var(--primary)' }}>
                      <CheckCircle size={16} />
                      Plan actual
                    </div>
                  ) : plan.name === 'trial' ? (
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Disponible al registrarse</p>
                  ) : (
                    <BaseButton
                      size="sm"
                      icon={ArrowSquareOut}
                      onClick={() => handleCheckout(plan.name)}
                      disabled={paying === plan.name}>
                      {paying === plan.name ? 'Abriendo pago…' : 'Contratar'}
                    </BaseButton>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default BillingModule;
